import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!token) { setErrorMsg("Missing reset token. Please use the link from your email."); return; }
    if (pw.length < 8) { setErrorMsg("Password must be at least 8 characters."); return; }
    if (pw !== pw2)     { setErrorMsg("Passwords do not match."); return; }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset. You can now sign in.");
      setTimeout(() => nav("/login"), 1600);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrorMsg(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-linen">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/login" data-testid="link-back-login"
                className="text-sm text-ink-mid hover:text-ink inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>

        {done ? (
          <div className="card-widget p-8 text-center" data-testid="reset-password-done">
            <div className="w-14 h-14 rounded-full bg-pine/10 text-ink grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl text-ink">Password reset</h1>
            <p className="mt-3 text-sm text-ink-mid">Redirecting you to sign in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="card-widget p-8" data-testid="reset-password-form">
            <div className="w-12 h-12 rounded-xl bg-pine text-linen grid place-items-center mb-5">
              <KeyRound className="w-5 h-5" />
            </div>
            <h1 className="font-display text-3xl text-ink leading-tight">Set a new password</h1>
            <p className="mt-3 text-sm text-ink-mid leading-relaxed">
              Choose a new password for your Viyora account. Minimum 8 characters. Use a mix
              of letters, numbers and symbols.
            </p>

            {!token && (
              <div className="mt-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  This link is missing a token. Please open the reset link from your email again,
                  or <Link to="/forgot-password" className="underline">request a new one</Link>.
                </div>
              </div>
            )}

            <label className="block mt-5">
              <span className="text-xs uppercase tracking-widest text-ink-mid">New password</span>
              <input
                type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                required minLength={8} autoFocus autoComplete="new-password"
                data-testid="reset-password-new"
                className="w-full mt-1 rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
              />
            </label>

            <label className="block mt-4">
              <span className="text-xs uppercase tracking-widest text-ink-mid">Confirm password</span>
              <input
                type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                required minLength={8} autoComplete="new-password"
                data-testid="reset-password-confirm"
                className="w-full mt-1 rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
              />
            </label>

            {errorMsg && (
              <div data-testid="reset-password-error"
                   className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>{errorMsg}</div>
              </div>
            )}

            <button
              type="submit" disabled={loading || !token}
              data-testid="reset-password-submit"
              className="mt-6 w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Reset password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
